<script setup>
import { ref, onMounted } from 'vue';
import MessageAPI from 'dashboard/api/inbox/message';
import crmAPI from 'dashboard/routes/dashboard/crm/api/crm';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const MAX_STICKERS = 20;
const showPopover = ref(false);
const stickers = ref([]);
const isLoading = ref(false);
const isImporting = ref(false);
const isSending = ref(false);
const fileInputRef = ref(null);

const fetchStickers = async () => {
  try {
    isLoading.value = true;
    const response = await crmAPI.getStickers();
    stickers.value = response.data?.stickers || [];
  } catch (err) {
    console.error('Erro ao buscar figurinhas:', err);
  } finally {
    isLoading.value = false;
  }
};

const togglePopover = () => {
  showPopover.value = !showPopover.value;
  if (showPopover.value && stickers.value.length === 0) {
    fetchStickers();
  }
};

const processImageToSticker = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 512;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        const scale = Math.min(size / img.width, size / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        const x = (size - w) / 2;
        const y = (size - h) / 2;

        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, x, y, w, h);

        const webpDataUrl = canvas.toDataURL('image/webp', 0.85);
        resolve(webpDataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const triggerFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
};

const onFileSelected = async e => {
  const file = e.target.files && e.target.files[0];
  if (!file) return;

  if (stickers.value.length >= MAX_STICKERS) {
    alert(`Limite de ${MAX_STICKERS} figurinhas atingido. Exclua uma figurinha para adicionar outra.`);
    return;
  }

  isImporting.value = true;
  try {
    const webpUrl = await processImageToSticker(file);
    const res = await crmAPI.createSticker({
      name: file.name.replace(/\.[^/.]+$/, ''),
      image_url: webpUrl,
      file_type: 'image/webp',
    });
    stickers.value.unshift(res.data);
  } catch (err) {
    console.error('Erro ao importar figurinha:', err);
    alert(err.response?.data?.error || 'Erro ao importar figurinha');
  } finally {
    isImporting.value = false;
    if (fileInputRef.value) fileInputRef.value.value = '';
  }
};

const deleteSticker = async id => {
  try {
    await crmAPI.deleteSticker(id);
    stickers.value = stickers.value.filter(s => s.id !== id);
  } catch (err) {
    console.error('Erro ao excluir figurinha:', err);
  }
};

const sendSticker = async sticker => {
  if (!props.conversationId || isSending.value) return;
  showPopover.value = false;
  isSending.value = true;

  try {
    const response = await fetch(sticker.image_url);
    const blob = await response.blob();
    const file = new File([blob], `sticker_${Date.now()}.webp`, { type: 'image/webp' });

    await MessageAPI.create({
      conversationId: props.conversationId,
      message: '',
      private: false,
      files: [file],
      echo_id: `temp-${Date.now()}`,
    });
  } catch (err) {
    console.error('Erro ao enviar figurinha:', err);
  } finally {
    isSending.value = false;
  }
};

onMounted(() => {
  // Preload sob demanda
});
</script>

<template>
  <div class="relative inline-block">
    <!-- Botão de Figurinhas -->
    <button
      type="button"
      class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800 transition-colors cursor-pointer text-sm"
      :class="showPopover ? 'text-amber-400 bg-slate-800' : ''"
      title="Figurinhas da Clínica (WhatsApp)"
      @click="togglePopover"
    >
      🎴
    </button>

    <!-- Popover Galeria de Figurinhas -->
    <div
      v-if="showPopover"
      class="absolute bottom-full left-0 mb-2 w-72 sm:w-80 bg-[#182229] border border-slate-700 rounded-2xl shadow-2xl overflow-hidden z-[9999] animate-fade-in flex flex-col max-h-80"
    >
      <!-- Header da Galeria -->
      <div class="px-3.5 py-2.5 border-b border-slate-800 bg-[#111b21] flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <span class="text-sm">🎴</span>
          <h4 class="text-xs font-bold text-white">Figurinhas</h4>
          <span class="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-slate-800 text-emerald-400 border border-emerald-500/30">
            {{ stickers.length }} / {{ MAX_STICKERS }}
          </span>
        </div>

        <div class="flex items-center gap-1.5">
          <input
            ref="fileInputRef"
            type="file"
            class="hidden"
            accept="image/webp,image/png,image/jpeg,image/gif"
            @change="onFileSelected"
          />
          <button
            type="button"
            :disabled="stickers.length >= MAX_STICKERS || isImporting"
            class="px-2 py-1 text-[10px] font-bold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg transition-all shadow-xs flex items-center gap-1 cursor-pointer"
            @click="triggerFileInput"
          >
            <span v-if="isImporting" class="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span v-else>+ Importar</span>
          </button>
          <button
            type="button"
            class="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 text-xs"
            @click="showPopover = false"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Grid de Figurinhas -->
      <div class="p-2.5 overflow-y-auto flex-1 max-h-56">
        <div v-if="isLoading" class="flex justify-center items-center py-8">
          <div class="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>

        <div v-else-if="stickers.length === 0" class="text-center py-6 text-xs text-slate-400 space-y-1">
          <p>Nenhuma figurinha salva.</p>
          <p class="text-[10px] text-slate-500">Clique em "+ Importar" para subir do PC!</p>
        </div>

        <div v-else class="grid grid-cols-4 gap-2">
          <div
            v-for="stk in stickers"
            :key="stk.id"
            class="relative group/stk p-1 rounded-xl bg-[#111b21] hover:bg-emerald-950/40 border border-slate-800 hover:border-emerald-500 transition-all flex items-center justify-center cursor-pointer"
            :title="`Enviar figurinha '${stk.name || 'Figurinha'}'`"
            @click="sendSticker(stk)"
          >
            <img
              :src="stk.image_url"
              class="w-14 h-14 object-contain rounded-lg transition-transform group-hover/stk:scale-105"
              :alt="stk.name"
            />
            <button
              type="button"
              class="absolute -top-1 -right-1 opacity-0 group-hover/stk:opacity-100 transition-opacity p-0.5 bg-rose-600 hover:bg-rose-500 text-white rounded-full text-[8px] shadow-md cursor-pointer"
              title="Excluir figurinha"
              @click.stop="deleteSticker(stk.id)"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
